/**
 * Cross-Border utilities — region colors, aging, status progression.
 *
 * Single source of truth used by both the CB tab, the CB kanban, and any
 * load card that wants to show "stuck for 18h" badges.
 */

// Cross-border status progression (in order)
export const CB_STATUS_FLOW = [
  'Available',
  'Covered',
  'Rate Con Sent',
  'At the Shipper',
  'In Transit MX',
  'At Border MX',
  'Drop Off Trailer',
  'At Border US',
  'In Transit US',
  'Delivered',
];

// Friendlier short labels for the visual stepper
export const CB_STEPPER_LABELS = {
  'At the Shipper': 'Pickup',
  'In Transit MX': 'Transit MX',
  'At Border MX': 'Border MX',
  'Drop Off Trailer': 'Drop Yard',
  'At Border US': 'Border US',
  'In Transit US': 'Transit US',
  'Delivered': 'Delivered',
};

// Statuses shown in the stepper (operators care about post-pickup phases)
export const CB_STEPPER_STATUSES = [
  'At the Shipper',
  'In Transit MX',
  'At Border MX',
  'Drop Off Trailer',
  'At Border US',
  'In Transit US',
  'Delivered',
];

/**
 * Region classification for a status. Used to color-tag by side-of-border.
 *  - 'mx'  : Mexican-side states
 *  - 'border' : trailer at or in the border yard (handoff)
 *  - 'us'  : US-side states
 *  - 'pre' : pre-pickup states (Available, Covered, Rate Con Sent, At the Shipper)
 *  - 'done': delivered / released
 */
export const getStatusRegion = (status) => {
  if (status === 'Delivered' || status === 'Released') return 'done';
  if (status === 'In Transit MX') return 'mx';
  if (status === 'At Border MX' || status === 'Drop Off Trailer') return 'border';
  if (status === 'At Border US' || status === 'In Transit US') return 'us';
  if (status === 'In Transit' || status === 'At Delivery') return 'us'; // domestic but treated as US-side
  return 'pre';
};

export const REGION_COLORS = {
  mx:     { bg: 'bg-orange-100',  fg: 'text-orange-700',  ring: 'ring-orange-200',  dot: 'bg-orange-500',  label: '🇲🇽 MX' },
  border: { bg: 'bg-yellow-100',  fg: 'text-yellow-800',  ring: 'ring-yellow-200',  dot: 'bg-yellow-500',  label: '⇆ Border' },
  us:     { bg: 'bg-sky-100',     fg: 'text-sky-700',     ring: 'ring-sky-200',     dot: 'bg-sky-500',     label: '🇺🇸 US' },
  pre:    { bg: 'bg-slate-100',   fg: 'text-slate-600',   ring: 'ring-slate-200',   dot: 'bg-slate-400',   label: 'Pre-Pickup' },
  done:   { bg: 'bg-emerald-100', fg: 'text-emerald-700', ring: 'ring-emerald-200', dot: 'bg-emerald-500', label: '✓ Delivered' },
};

/**
 * Compute how long a load has been at its current status.
 * Returns { hours, level: 'fresh'|'warn'|'stuck', label: '18h' }
 *
 * Thresholds:
 *  - < 8h   = fresh (green)
 *  - 8-24h  = warn (yellow)
 *  - > 24h  = stuck (red)
 */
export const getAgingInfo = (load) => {
  const stamp = load?.status_changed_at || load?.updated_at || load?.created_at;
  if (!stamp) return null;

  const then = new Date(stamp).getTime();
  if (isNaN(then)) return null;

  const ms = Date.now() - then;
  const hours = Math.floor(ms / (1000 * 60 * 60));

  let level = 'fresh';
  if (hours > 24) level = 'stuck';
  else if (hours >= 8) level = 'warn';

  let label;
  if (hours < 1) {
    const mins = Math.max(1, Math.floor(ms / 60000));
    label = `${mins}m`;
  } else if (hours < 24) {
    label = `${hours}h`;
  } else {
    const days = Math.floor(hours / 24);
    const rem = hours % 24;
    label = rem > 0 ? `${days}d ${rem}h` : `${days}d`;
  }

  return { hours, level, label };
};

export const AGING_STYLES = {
  fresh: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn:  'bg-amber-50 text-amber-700 border-amber-200',
  stuck: 'bg-red-50 text-red-700 border-red-200 ring-1 ring-red-200',
};

/**
 * Is a CB load "stuck" — at a non-terminal status for > 24h?
 * Terminal = Delivered, Released.
 */
export const isLoadStuck = (load) => {
  if (!load || !load.cross_border) return false;
  if (load.status === 'Delivered' || load.status === 'Released') return false;
  const info = getAgingInfo(load);
  return info ? info.level === 'stuck' : false;
};
